import { z } from 'zod';

import type { InitialUtm } from '@/api/core-api/types.gen';

/**
 * The UTM shape, written down once.
 *
 * Campaign attribution travels through four boundaries — the URL, the
 * participant-wide `localStorage` store, the per-project cookie, and the
 * `initialUtm` body core-api takes on signup and on a screener save — and each
 * of them used to spell the field list out again. Any one of them could be
 * updated alone, and a field missed at any single hop is silently dropped
 * attribution: no error, just a campaign that stops being reported.
 *
 * So the list lives here, keyed by the field core-api expects and valued with
 * the `utm_*` key that carries it in a URL. The browser-side stores hold URL
 * keys rather than API fields, so a stored value reads back exactly as it
 * arrived, and the conversion happens once, on the way out.
 *
 * `satisfies Record<keyof InitialUtm, …>` is what makes this the source of
 * truth rather than one more copy: it is checked against the generated
 * core-api type, so a field added, renamed or removed upstream stops this file
 * compiling. Everything below derives from the table, and `utm.test.ts` holds
 * the two zod schemas to it.
 */
export const UTM_SEARCH_KEY_BY_FIELD = {
  source: 'utm_source',
  medium: 'utm_medium',
  campaign: 'utm_campaign',
  term: 'utm_term',
  content: 'utm_content',
  advertId: 'utm_advert_id',
  utmId: 'utm_id',
} as const satisfies Record<keyof InitialUtm, `utm_${string}`>;

/**
 * `Object.entries` widens the key to `string`, which loses the one thing the
 * table is for. Narrowed back here, once, over a local literal we own.
 */
const UTM_FIELD_ENTRIES = Object.entries(
  UTM_SEARCH_KEY_BY_FIELD,
) as ReadonlyArray<readonly [keyof InitialUtm, UtmSearchKey]>;

/** A `utm_*` key, as it appears in a URL and in the browser-side stores. */
export type UtmSearchKey =
  (typeof UTM_SEARCH_KEY_BY_FIELD)[keyof typeof UTM_SEARCH_KEY_BY_FIELD];

export const UTM_SEARCH_KEYS: readonly UtmSearchKey[] = Object.values(
  UTM_SEARCH_KEY_BY_FIELD,
);

/** Anything that might carry `utm_*` values: URL search, a parsed store. */
export type UtmSearch = Partial<Record<UtmSearchKey, string | undefined>>;

/** Longer than any real campaign tag, and a bound on what we store and send. */
export const MAX_UTM_VALUE_LENGTH = 512;

/** Trimmed and length-bounded, or undefined when there is nothing there. */
export function clampUtmValue(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  return trimmed.length > MAX_UTM_VALUE_LENGTH
    ? trimmed.slice(0, MAX_UTM_VALUE_LENGTH)
    : trimmed;
}

/**
 * The `utm_*` values worth keeping, clamped, keyed as they arrived.
 *
 * Takes `unknown` values so the same reader serves URL search params and a
 * JSON store that could hold anything: a hand-edited cookie or a stale
 * `localStorage` envelope contributes nothing rather than a non-string.
 */
export function pickUtmValues(
  source: Partial<Record<UtmSearchKey, unknown>> | undefined,
): Partial<Record<UtmSearchKey, string>> {
  if (!source) return {};

  const picked: Partial<Record<UtmSearchKey, string>> = {};
  for (const key of UTM_SEARCH_KEYS) {
    const raw = source[key];
    const value = typeof raw === 'string' ? clampUtmValue(raw) : undefined;
    if (value) picked[key] = value;
  }
  return picked;
}

/**
 * URL keys to the `initialUtm` core-api takes, with absent keys spelled as
 * `null` — the shape its `InitialUtm` sub-document stores.
 *
 * Undefined when there is nothing to attribute, because that is what every
 * caller must send in that case: an object of nulls would read as a campaign
 * in the leads reporting.
 */
export function toInitialUtm(
  source: Partial<Record<UtmSearchKey, unknown>> | undefined,
): InitialUtm | undefined {
  const values = pickUtmValues(source);
  if (Object.keys(values).length === 0) return undefined;

  const initialUtm: InitialUtm = {};
  for (const [field, searchKey] of UTM_FIELD_ENTRIES) {
    initialUtm[field] = values[searchKey] ?? null;
  }
  return initialUtm;
}

/**
 * The `utm_*` params on a route, for `validateSearch`. Extend it with a
 * route's own params rather than restating the UTM keys.
 *
 * Written out rather than derived so the inferred search type stays exact —
 * building the shape dynamically collapses it to an index signature, which
 * would let a mistyped search key through anywhere this is extended. The key
 * list is held to the table by `utm.test.ts`.
 */
export const utmSearchSchema = z.object({
  utm_source: z.coerce.string().optional(),
  utm_medium: z.coerce.string().optional(),
  utm_campaign: z.coerce.string().optional(),
  utm_term: z.coerce.string().optional(),
  utm_content: z.coerce.string().optional(),
  utm_advert_id: z.coerce.string().optional(),
  utm_id: z.coerce.string().optional(),
});

/**
 * The `utm_*` values from a search object that has *not* been through a
 * route's `validateSearch` — `location.search` inside a route guard, say.
 *
 * TanStack's `parseSearch` types values by shape, so `?utm_advert_id=123`
 * arrives as the number `123` and {@link pickUtmValues} drops it: that reader
 * is deliberately strict because it also serves the browser stores, which can
 * hold anything. Coercing through {@link utmSearchSchema} first is what a
 * route already does for its own params, so a URL built in a guard carries the
 * same values the destination route would have captured.
 *
 * Never throws: a value no coercion can handle contributes nothing rather than
 * failing the redirect this rides on.
 */
export function pickUtmValuesFromRawSearch(
  search: Record<string, unknown> | undefined,
): Partial<Record<UtmSearchKey, string>> {
  if (!search) return {};
  const coerced = utmSearchSchema.safeParse(search);
  return pickUtmValues(coerced.success ? coerced.data : search);
}

/**
 * The `initialUtm` body field, for the BFF routes that forward it to core-api.
 * Nulls are accepted because that is how {@link toInitialUtm} spells "this key
 * was absent", and every field is clamped to the same bound the stores use.
 *
 * Same reason as above for writing the fields out: the request types inferred
 * from the routes' schemas stay exact, and the test holds this to the table.
 */
const utmBodyValue = z.string().max(MAX_UTM_VALUE_LENGTH).nullish();

export const initialUtmSchema = z.object({
  source: utmBodyValue,
  medium: utmBodyValue,
  campaign: utmBodyValue,
  term: utmBodyValue,
  content: utmBodyValue,
  advertId: utmBodyValue,
  utmId: utmBodyValue,
});
